/**
 * Створення звіту про внесок на підтримку працевлаштування осіб з інвалідністю
 * (ч.1208): організація, рік і календарний квартал. Форма (J0500701 / F0500701)
 * обирається сервером з картки організації; чисельність, норматив і внесок
 * рахуються з кадрів і проведених нарахувань. Підпис і відправка — у Кабінеті ДПС.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { QUARTER_LABELS, disabilityContributionReports } from '@/api/disabilityContributionReports';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';

/** Попередній завершений квартал відносно дати (звіт подають після кварталу). */
export function previousQuarter(now: Date): { year: number; quarter: number } {
  const q = Math.floor(now.getMonth() / 3) + 1;
  return q === 1 ? { year: now.getFullYear() - 1, quarter: 4 } : { year: now.getFullYear(), quarter: q - 1 };
}

export default function DisabilityContributionReportCreatePage() {
  const navigate = useNavigate();
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } = usePermissionsByOrg('payroll.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const prev = previousQuarter(new Date());
  const firstYear = 2026;
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(Math.max(prev.year, firstYear));
  const [quarter, setQuarter] = useState(prev.year < firstYear ? 1 : prev.quarter);
  const [reportType, setReportType] = useState<'REPORTING' | 'REPORTING_NEW' | 'CORRECTING'>('REPORTING');
  const [corrYear, setCorrYear] = useState(Math.max(prev.year, firstYear));
  const [corrQuarter, setCorrQuarter] = useState(prev.year < firstYear ? 1 : prev.quarter);
  const [error, setError] = useState<string | null>(null);
  const isCorrection = reportType === 'CORRECTING';
  const effectiveOrg = orgId
    || (availableOrgs.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || availableOrgs[0]?.id || '';

  const create = useMutation({
    mutationFn: () => disabilityContributionReports.create({
      organization_id: effectiveOrg, period_year: year, period_quarter: quarter,
      report_type: reportType,
      ...(isCorrection ? { corrected_period_year: corrYear, corrected_period_quarter: corrQuarter } : {}),
    }),
    onSuccess: (doc) => navigate(`/taxation/disability-contribution-reports/${doc.id}`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  if (!hasPermissionInAnyOrg) {
    return <div className="card">У вас немає прав створювати звіт у жодній з {totalAccessibleOrgs} організацій.</div>;
  }
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + 1 - i).filter((y) => y >= firstYear);
  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Новий звіт про внесок (особи з інвалідністю)</h2>
        <button style={{ marginLeft: 'auto' }} onClick={() => navigate('/dps-cabinet/new')}>до каталогу</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
        Звіт подають щокварталу. Чисельність береться з карток працівників і наказів про
        прийом та звільнення, зарплата — з проведених нарахувань за квартал. Норматив: від 8 до
        25 працівників — одне робоче місце, більше 25 — 4 % середньооблікової кількості
        (2 % для закладів охорони здоров’я і реабілітації). Якщо норматив не виконано,
        нараховується внесок; якщо працівників менше восьми або норматив виконано — звіт
        іде без розрахунку. Підпис і відправка — у Журналі Кабінету ДПС.
      </p>
      <div className="field">
        <label>Організація</label>
        <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)}>
          {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div className="field">
        <label>Тип</label>
        <select value={reportType} onChange={(e) => setReportType(e.target.value as any)}>
          <option value="REPORTING">Звітний</option>
          <option value="REPORTING_NEW">Звітний новий</option>
          <option value="CORRECTING">Уточнюючий</option>
        </select>
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>{isCorrection ? 'Квартал подання' : 'Квартал'}</label>
          <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
            {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{QUARTER_LABELS[q]}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Рік</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      {isCorrection && (
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Квартал, що уточнюється</label>
            <select value={corrQuarter} onChange={(e) => setCorrQuarter(Number(e.target.value))}>
              {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{QUARTER_LABELS[q]}</option>)}
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Рік, що уточнюється</label>
            <select value={corrYear} onChange={(e) => setCorrYear(Number(e.target.value))}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      )}
      {error && <div style={{ color: 'var(--badge-red-fg)', margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}>{error}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <button className="primary" disabled={!effectiveOrg || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'рахуємо…' : 'створити і розрахувати'}
        </button>
        <button onClick={() => navigate('/taxation/disability-contribution-reports')}>скасувати</button>
      </div>
    </div>
  );
}
